/**
 * useDotPlotData - Scatter plot data computation for PerformanceAnalytics
 *
 * Extracts the scatter plot (dot plot) data preparation from the Container
 * to reduce file length. Computes background players, comparison player data,
 * combined scatter plot players, tooltip formatter, and mobile sizing.
 * @remarks This hook is consumed only by PerformanceAnalyticsContainer
 */

import React, { useMemo, useCallback } from "react";

import DotPlot from "@/features/player-detail/components/molecules/DotPlot";
import {
  extractComparisonPlayerScores,
  generateScatterPlotPlayers,
} from "@/features/player-detail/utils/performanceAnalyticsUtils";
import type { MetricOption } from "@/features/player-detail/utils/performanceAnalyticsUtils";
import type { PerformanceShellTriggerProps } from "@/features/player-detail/hooks/usePerformanceMenuTriggers";
import type { ShellDropdownWiring } from "@/features/player-detail/utils/performanceAnalyticsViewHelpers";
import type { CompareItem } from "@/features/player-detail/types";
import { DOT_PLOT_SIZE } from "@/features/player-detail/constants";
import type { PerformanceAnalyticsPlayerScoresFragment } from "@/generated/graphql";

/** Internal padding per card side (p-3 = 12px × 2) */
const MOBILE_CARD_CONTENT_PADDING = 24;

/**
 * Props for the useDotPlotData hook
 */
interface UseDotPlotDataProps {
  /** Whether currently comparing against a group (position, league, etc.) */
  readonly isGroupComparison: boolean;
  /** Players fetched from the comparison data source */
  readonly comparisonPlayers:
    | readonly PerformanceAnalyticsPlayerScoresFragment[]
    | null
    | undefined;
  /** Players fetched from the position data source */
  readonly positionPlayers:
    | readonly PerformanceAnalyticsPlayerScoresFragment[]
    | null
    | undefined;
  /** Season key for extracting season-specific scores */
  readonly seasonKey: string | undefined;
  /** Whether a specific player (not group) is selected for comparison */
  readonly hasActualPlayerSelected: boolean;
  /** The selected compare item (player) for the orange dot */
  readonly selectedCompareItem: CompareItem | null;
  /** Comparison averages keyed by metric label */
  readonly comparisonAverages: Record<string, number | null>;
  /** Main player's display name */
  readonly playerName: string;
  /** X-axis metric with current value */
  readonly xMetricWithValue: MetricOption;
  /** Y-axis metric with current value */
  readonly yMetricWithValue: MetricOption;
  /** Main player's scores keyed by metric label */
  readonly playerScores: Record<string, number | null>;
  /** Available metrics for axis selection */
  readonly availableMetrics: readonly MetricOption[];
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Whether the screen is mobile sized */
  readonly isSmallScreen: boolean;
  /** Card width for mobile layout */
  readonly cardWidth: number;
  /** Desktop dot plot size from responsive measurement (falls back to DOT_PLOT_SIZE) */
  readonly desktopDotPlotSize?: number;
  /** Render function for Y-axis menu trigger (shell-hosted, SE-5568) */
  readonly renderYMenuTrigger: (
    props: PerformanceShellTriggerProps
  ) => React.ReactElement;
  /** Render function for X-axis menu trigger (shell-hosted, SE-5568) */
  readonly renderXMenuTrigger: (
    props: PerformanceShellTriggerProps
  ) => React.ReactElement;
  /** Factory for creating Y-axis metric select handlers */
  readonly createYSelectHandler: (m: MetricOption) => () => void;
  /** Factory for creating X-axis metric select handlers */
  readonly createXSelectHandler: (m: MetricOption) => () => void;
  /** Shell wiring for the X-axis dropdown (SE-5568). */
  readonly xAxisDropdown: ShellDropdownWiring;
  /** Shell wiring for the Y-axis dropdown (SE-5568). */
  readonly yAxisDropdown: ShellDropdownWiring;
}

/**
 * Return type for useDotPlotData hook
 */
interface UseDotPlotDataReturn {
  /** Mobile dot plot size in pixels */
  readonly mobileDotPlotSize: number;
  /** Combined scatter plot player data points */
  readonly scatterPlotPlayers: readonly {
    id: string;
    name: string;
    x: number | null;
    y: number | null;
    isCurrentPlayer: boolean;
    isComparisonPlayer?: boolean;
  }[];
  /** Tooltip label formatter for DotPlot */
  readonly dotPlotTooltipLabelFormatter: (args: {
    datum: { name: string; x: number | null; y: number | null };
  }) => string;
  /** Pre-rendered DotPlot content for desktop layout */
  readonly dotPlotContent: React.ReactNode;
}

/**
 * Computes scatter plot data, tooltip formatter, and pre-rendered DotPlot content
 * @param props - Hook props
 * @param props.isGroupComparison - Whether comparing against a group
 * @param props.comparisonPlayers - Players from the comparison source
 * @param props.positionPlayers - Players from the position source
 * @param props.seasonKey - Season key for score extraction
 * @param props.hasActualPlayerSelected - Whether a specific player is selected
 * @param props.selectedCompareItem - Selected comparison item
 * @param props.comparisonAverages - Comparison averages by metric
 * @param props.playerName - Main player display name
 * @param props.xMetricWithValue - X-axis metric
 * @param props.yMetricWithValue - Y-axis metric
 * @param props.playerScores - Main player scores
 * @param props.availableMetrics - Available metrics for selection
 * @param props.isDark - Whether dark mode is active
 * @param props.isSmallScreen - Whether the screen is mobile sized
 * @param props.cardWidth - Card width for mobile layout
 * @param props.desktopDotPlotSize - Desktop dot plot size from responsive measurement
 * @param props.renderYMenuTrigger - Y-axis menu trigger render function
 * @param props.renderXMenuTrigger - X-axis menu trigger render function
 * @param props.createYSelectHandler - Y-axis metric select handler factory
 * @param props.createXSelectHandler - X-axis metric select handler factory
 * @param props.xAxisDropdown - Shell wiring for the X-axis dropdown (SE-5568)
 * @param props.yAxisDropdown - Shell wiring for the Y-axis dropdown (SE-5568)
 * @returns Scatter plot data, tooltip formatter, size, and pre-rendered content
 */
export const useDotPlotData = ({
  isGroupComparison,
  comparisonPlayers,
  positionPlayers,
  seasonKey,
  hasActualPlayerSelected,
  selectedCompareItem,
  comparisonAverages,
  playerName,
  xMetricWithValue,
  yMetricWithValue,
  playerScores,
  availableMetrics,
  isDark,
  isSmallScreen,
  cardWidth,
  desktopDotPlotSize,
  renderYMenuTrigger,
  renderXMenuTrigger,
  createYSelectHandler,
  createXSelectHandler,
  xAxisDropdown,
  yAxisDropdown,
}: UseDotPlotDataProps): UseDotPlotDataReturn => {
  const scatterPlotBackgroundPlayers = useMemo(() => {
    const playersToUse = isGroupComparison
      ? comparisonPlayers
      : positionPlayers;
    return (
      playersToUse?.map(p => ({
        id: p.id,
        name: p?.name ?? "",
        scores: extractComparisonPlayerScores(p, seasonKey),
      })) ?? []
    );
  }, [isGroupComparison, comparisonPlayers, positionPlayers, seasonKey]);

  const comparisonPlayerData = useMemo(
    () =>
      hasActualPlayerSelected && selectedCompareItem
        ? {
            id: selectedCompareItem.id,
            name: selectedCompareItem.name,
            scores: comparisonAverages,
          }
        : null,
    [hasActualPlayerSelected, selectedCompareItem, comparisonAverages]
  );

  const scatterPlotPlayers = useMemo(
    () =>
      generateScatterPlotPlayers(
        playerName,
        xMetricWithValue.label,
        yMetricWithValue.label,
        playerScores,
        scatterPlotBackgroundPlayers,
        comparisonPlayerData
      ),
    [
      playerName,
      xMetricWithValue.label,
      yMetricWithValue.label,
      playerScores,
      scatterPlotBackgroundPlayers,
      comparisonPlayerData,
    ]
  );

  const dotPlotTooltipLabelFormatter = useCallback(
    ({
      datum,
    }: {
      datum: { name: string; x: number | null; y: number | null };
    }): string => {
      const xDisplay = datum.x != null ? datum.x : "--";
      const yDisplay = datum.y != null ? datum.y : "--";
      return `${datum.name}\n${xMetricWithValue.label}: ${xDisplay}\n${yMetricWithValue.label}: ${yDisplay}`;
    },
    [xMetricWithValue.label, yMetricWithValue.label]
  );

  const mobileDotPlotSize = isSmallScreen
    ? cardWidth - MOBILE_CARD_CONTENT_PADDING
    : (desktopDotPlotSize ?? DOT_PLOT_SIZE);

  const dotPlotContent = useMemo(
    () => (
      <DotPlot
        players={scatterPlotPlayers}
        xMetric={xMetricWithValue}
        yMetric={yMetricWithValue}
        availableMetrics={availableMetrics}
        isDark={isDark}
        size={mobileDotPlotSize}
        renderYMenuTrigger={renderYMenuTrigger}
        renderXMenuTrigger={renderXMenuTrigger}
        createYSelectHandler={createYSelectHandler}
        createXSelectHandler={createXSelectHandler}
        xAxisDropdown={xAxisDropdown}
        yAxisDropdown={yAxisDropdown}
        tooltipLabelFormatter={dotPlotTooltipLabelFormatter}
      />
    ),
    [
      scatterPlotPlayers,
      xMetricWithValue,
      yMetricWithValue,
      availableMetrics,
      isDark,
      mobileDotPlotSize,
      renderYMenuTrigger,
      renderXMenuTrigger,
      createYSelectHandler,
      createXSelectHandler,
      xAxisDropdown,
      yAxisDropdown,
      dotPlotTooltipLabelFormatter,
    ]
  );

  return {
    mobileDotPlotSize,
    scatterPlotPlayers,
    dotPlotTooltipLabelFormatter,
    dotPlotContent,
  };
};
